export type DistributionShape =
  | 'uniform'
  | 'normal'
  | 'right-skewed'
  | 'left-skewed'
  | 'bimodal'
  | 'exp-decay'
  | 'exp-growth'
  | 'u-shaped';

export interface DataGeneratorConfig {
  readonly distribution: DistributionShape;
  readonly sampleSize: number;
  readonly minValue: number;
  readonly maxValue: number;
  readonly mean: number;
  readonly stdDev: number;
  readonly center1: number;
  readonly center2: number;
  readonly spread: number;
  readonly outliers: number;
  readonly decimalPlaces: number;
  readonly seed: string;
  readonly integerOnly: boolean;
  readonly columnLabel: string;
}

/** Distributions that use mean/stdDev instead of min/max */
export const MEAN_SD_DISTRIBUTIONS: ReadonlySet<DistributionShape> = new Set(['normal']);

/** Distributions that use center1/center2/spread */
export const BIMODAL_DISTRIBUTIONS: ReadonlySet<DistributionShape> = new Set(['bimodal']);

export const DEFAULT_GENERATOR_CONFIG: DataGeneratorConfig = {
  distribution: 'normal',
  sampleSize: 50,
  minValue: 0,
  maxValue: 100,
  mean: 50,
  stdDev: 15,
  center1: 30,
  center2: 70,
  spread: 10,
  outliers: 0,
  decimalPlaces: 0,
  seed: '',
  integerOnly: false,
  columnLabel: 'x',
};

export const SAMPLE_SIZE_PRESETS = [10, 25, 50, 100] as const;
export const DECIMAL_PLACES_OPTIONS = [0, 1, 2, 3] as const;

export interface DistributionCardDef {
  readonly shape: DistributionShape;
  readonly label: string;
  readonly svgPath: string;
}

/**
 * SVG path data for each distribution curve icon.
 * All paths are drawn within a 60×30 viewBox.
 */
export const DISTRIBUTION_CARDS: readonly DistributionCardDef[] = [
  {
    shape: 'uniform',
    label: 'Uniform',
    svgPath: 'M 5 25 L 5 8 L 55 8 L 55 25',
  },
  {
    shape: 'normal',
    label: 'Normal',
    svgPath: 'M 5 25 Q 15 25 20 15 Q 25 5 30 5 Q 35 5 40 15 Q 45 25 55 25',
  },
  {
    shape: 'right-skewed',
    label: 'Right-skewed',
    svgPath: 'M 5 25 Q 10 25 13 10 Q 15 5 18 8 Q 25 15 35 20 Q 45 24 55 25',
  },
  {
    shape: 'left-skewed',
    label: 'Left-skewed',
    svgPath: 'M 5 25 Q 15 24 25 20 Q 35 15 42 8 Q 45 5 47 10 Q 50 25 55 25',
  },
  {
    shape: 'bimodal',
    label: 'Bimodal',
    svgPath: 'M 5 25 Q 10 25 13 12 Q 16 5 19 12 Q 22 20 30 20 Q 38 20 41 12 Q 44 5 47 12 Q 50 25 55 25',
  },
  {
    shape: 'exp-decay',
    label: 'Exp. decay',
    svgPath: 'M 5 5 C 8 5 10 18 16 22 C 22 25 35 25 55 25',
  },
  {
    shape: 'exp-growth',
    label: 'Exp. growth',
    svgPath: 'M 5 25 C 25 25 38 25 44 22 C 50 18 52 5 55 5',
  },
  {
    shape: 'u-shaped',
    label: 'U-shaped',
    svgPath: 'M 5 5 C 8 12 12 22 18 25 C 24 27 36 27 42 25 C 48 22 52 12 55 5',
  },
];
